import { useCallback, useMemo } from 'react';
import { TaskNodeBase } from '@nexelem/graphne-api';
import { useKanbanEventStore } from '../../../../components/organisms/ProductionPlans/Kanban/store';
import { selectGanttInstanceData, useGanttStore } from '../../../../components/organisms/ProductionPlans/Gantt/store';
import { useGroupedQueuesStore, visibleQueuesSelector } from '../../../../components/organisms/ProductionPlans/store';
import { useGanttFilterStore } from '../../../../components/organisms/ProductionPlans/hooks/useGanttFilterStore';
import { isWithinInterval } from 'date-fns';
import { useGanttInstanceId } from '../../../../components/organisms/ProductionPlans/Gantt/utils';

type TaskAvailabilityReason = 'differentQueue' | 'timeRange' | null;

type TaskAvailabilityInfo = {
  isAvailable: boolean;
  reason: TaskAvailabilityReason;
};

const checkGanttAvailability = (
  instanceId: string,
  task: TaskNodeBase,
  visibleQueueIds: number[],
): TaskAvailabilityInfo => {
  const taskQueueId = task.queue?.id;

  // Check if task's queue is visible
  if (!taskQueueId || !visibleQueueIds.includes(taskQueueId)) {
    return { isAvailable: false, reason: 'differentQueue' };
  }

  // Check if task has end date (required for time range validation)
  if (!task.end || !task.start) {
    return { isAvailable: false, reason: 'timeRange' };
  }

  // Get current date range from filter store
  const { ganttDateRange } = useGanttFilterStore.getState();

  // Check if task is within the current date range
  const taskStart = new Date(task.start);
  const taskEnd = new Date(task.end);

  const isInDateRange =
    isWithinInterval(taskStart, { start: ganttDateRange.from, end: ganttDateRange.to }) ||
    isWithinInterval(taskEnd, { start: ganttDateRange.from, end: ganttDateRange.to }) ||
    (taskStart < ganttDateRange.from && taskEnd > ganttDateRange.to);

  if (!isInDateRange) {
    return { isAvailable: false, reason: 'timeRange' };
  }

  // Check if task exists in rendered queues
  const ganttStore = useGanttStore.getState();
  const queue = selectGanttInstanceData(instanceId)(ganttStore).queues.find((q) => q.id === taskQueueId);

  if (!queue) {
    return { isAvailable: false, reason: 'differentQueue' };
  }

  const taskExists = queue.queueNodes.some((node) => node.id === task.id);

  if (!taskExists) {
    return { isAvailable: false, reason: 'timeRange' };
  }

  return { isAvailable: true, reason: null };
};

const checkKanbanAvailability = (task: TaskNodeBase, visibleQueueIds: number[]): TaskAvailabilityInfo => {
  const taskQueueId = task.queue?.id;

  // Check if task's queue is visible in UI
  if (!taskQueueId || !visibleQueueIds.includes(taskQueueId)) {
    return { isAvailable: false, reason: 'differentQueue' };
  }

  // Check if task exists in Kanban store
  const kanbanStore = useKanbanEventStore.getState();
  const queue = kanbanStore.data?.productionQueues?.find((q) => q.queue.id === taskQueueId);

  if (!queue) {
    return { isAvailable: false, reason: 'differentQueue' };
  }

  const taskExists = queue.tasks?.some((t) => t.tpe === 'Task' && t.id === task.id);

  if (!taskExists) {
    return { isAvailable: false, reason: 'timeRange' };
  }

  return { isAvailable: true, reason: null };
};

export const useTaskAvailability = (isKanban: boolean) => {
  const instanceId = useGanttInstanceId();
  const visibleQueues = useGroupedQueuesStore(visibleQueuesSelector);
  const visibleQueueIds = useMemo(
    () => visibleQueues.map((q) => q.id).filter((id): id is number => id !== undefined),
    [visibleQueues],
  );

  const getTaskAvailabilityInfo = useCallback(
    (task: TaskNodeBase & { nodeType: 'TaskNode' }): TaskAvailabilityInfo =>
      isKanban
        ? checkKanbanAvailability(task, visibleQueueIds)
        : checkGanttAvailability(instanceId, task, visibleQueueIds),
    [isKanban, instanceId, visibleQueueIds],
  );

  return { getTaskAvailabilityInfo };
};
